/**
 * ContentBlocklistPage — Story 3.7: Admin content blocklist management (AC #3).
 *
 * Displays the current content blocklist for staff:admin review.
 * Staff can add/remove terms and save the updated list (atomic PUT replaces entire blocklist).
 *
 * Key points:
 *   - Atomic PUT: the full list is sent on each save (no partial updates)
 *   - Shows updatedBy, updatedAt, version per entry
 *   - Non-PHI: blocklist terms are vocabulary, not member-identifying data
 *   - Changes take effect within ≤60s via EventBridge hot-reload
 *
 * URL routing (FIX 3 — supervised pass):
 *   Uses VITE_SERVICES_BASE_URL (shared API gateway) + /v1/chat/admin/... gateway path.
 *   Gateway route: ANY /v1/chat/{proxy+} → rewrite /v1/$request.path.proxy
 *   Client /v1/chat/admin/blocklist → proxy=admin/blocklist → /v1/admin/blocklist ✓
 *   (pr #272 wires VITE_SERVICES_BASE_URL into admin-portal CI build; 3.7 merges after #272)
 *
 * @Auth: staff:admin role required (RequireAuth in app.tsx routing).
 * @NonPhiEndpoint — blocklist terms are clinical vocabulary, not member PHI.
 */

import { usePortalAuth } from '@nrx/auth-ui';
import { Table } from '@nrx/ui';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';

const SERVICES_BASE_URL: string =
  (import.meta.env['VITE_SERVICES_BASE_URL'] as string | undefined) ?? '';

// ─── Types ────────────────────────────────────────────────────────────────────

interface BlocklistEntry {
  id: string;
  term: string;
  category: string;
  addedBy: string;
  addedAt: string;
  version: number;
}

interface BlocklistResponse {
  entries: BlocklistEntry[];
  version: number;
  updatedBy: string;
  updatedAt: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function ContentBlocklistPage(): JSX.Element {
  const auth = usePortalAuth();
  const accessToken = auth.user?.access_token;

  const [entries, setEntries] = useState<BlocklistEntry[]>([]);
  const [meta, setMeta] = useState<{
    version: number;
    updatedBy: string;
    updatedAt: string;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  // Add form
  const [newTerm, setNewTerm] = useState('');
  const [newCategory, setNewCategory] = useState('supplement');

  // Save state
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    void fetchBlocklist();
  }, [auth]);

  async function fetchBlocklist(): Promise<void> {
    if (!accessToken) {
      setFetchError('Not authenticated — please sign in again.');
      setLoading(false);
      return;
    }
    try {
      const res = await fetch(`${SERVICES_BASE_URL}/v1/chat/admin/blocklist`, {
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      });
      if (!res.ok) {
        setFetchError(`Failed to load blocklist (${res.status})`);
        setLoading(false);
        return;
      }
      const data = (await res.json()) as BlocklistResponse;
      setEntries(data.entries ?? []);
      setMeta({ version: data.version, updatedBy: data.updatedBy, updatedAt: data.updatedAt });
    } catch {
      setFetchError('Unable to connect to API.');
    } finally {
      setLoading(false);
    }
  }

  function handleAddTerm(): void {
    const trimmed = newTerm.trim();
    if (!trimmed) return;
    const newEntry: BlocklistEntry = {
      id: `local-${Date.now()}`,
      term: trimmed,
      category: newCategory,
      addedBy: '',
      addedAt: new Date().toISOString(),
      version: 1,
    };
    setEntries((prev) => [...prev, newEntry]);
    setNewTerm('');
    setSaveSuccess(false);
  }

  function handleRemoveEntry(id: string): void {
    setEntries((prev) => prev.filter((e) => e.id !== id));
    setSaveSuccess(false);
  }

  async function handleSave(): Promise<void> {
    if (!accessToken) return;
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    const payload = {
      entries: entries.map((e) => ({ term: e.term, category: e.category })),
      // Required by BlocklistUpdateRequest contract for optimistic locking.
      // meta?.version is populated from the last GET response; 0 = initial creation.
      expectedVersion: meta?.version ?? 0,
    };

    try {
      const res = await fetch(`${SERVICES_BASE_URL}/v1/chat/admin/blocklist`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        setSaveError(`Save failed (${res.status})`);
        return;
      }
      setSaveSuccess(true);
      // Refresh to get server-assigned ids + updated version
      await fetchBlocklist();
    } catch {
      setSaveError('Unable to connect to API.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="p-8 max-w-4xl">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Content Blocklist</h1>
        <p className="mt-1 text-sm text-gray-500">
          Manage Betty's content blocklist. Terms are matched case-insensitively in member messages.
          Changes take effect within 60 seconds for new chat sessions.
        </p>
      </div>

      {/* Non-PHI notice */}
      <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
        <p className="text-xs text-blue-700">
          <strong>Non-PHI data:</strong> Blocklist terms are clinical vocabulary (e.g. medication
          names, supplement dosing terms). No member-identifying information is stored here.
        </p>
      </div>

      {meta !== null && (
        <div className="mt-3 p-3 bg-gray-50 rounded text-xs text-gray-500 border border-gray-200">
          Version {meta.version} · Last updated by {meta.updatedBy} at{' '}
          {new Date(meta.updatedAt).toLocaleString()}
        </div>
      )}

      {fetchError !== null && <p className="mt-6 text-red-600">{fetchError}</p>}

      {fetchError === null && (
        <div className="mt-6 space-y-6">
          {!loading && (
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h2 className="text-sm font-semibold text-gray-700 mb-3">Add Term</h2>
              <div className="flex gap-3 items-end">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Term</label>
                  <input
                    type="text"
                    value={newTerm}
                    onChange={(e) => setNewTerm(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddTerm();
                      }
                    }}
                    className="border border-gray-300 rounded text-sm px-3 py-1.5 w-48"
                    placeholder="e.g. berberine"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
                  <select
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value)}
                    className="border border-gray-300 rounded text-sm px-3 py-1.5"
                  >
                    <option value="supplement">supplement</option>
                    <option value="medication">medication</option>
                    <option value="clinical-term">clinical-term</option>
                    <option value="other">other</option>
                  </select>
                </div>
                <button
                  type="button"
                  onClick={handleAddTerm}
                  className="px-4 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded hover:bg-indigo-700"
                >
                  Add
                </button>
              </div>
            </div>
          )}

          {!loading && entries.length === 0 ? (
            <p className="text-gray-500 text-sm">No blocklist terms. Add terms above.</p>
          ) : (
            <Table.ScrollArea loading={loading} loadingLabel="Loading…">
              <Table.Root>
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeaderCell>Term</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Category</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Added By</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Action</Table.ColumnHeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {entries.map((entry) => (
                    <Table.Row key={entry.id} className="hover:bg-gray-50">
                      <Table.Cell className="font-mono text-gray-800">{entry.term}</Table.Cell>
                      <Table.Cell>
                        <span className="rounded bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
                          {entry.category}
                        </span>
                      </Table.Cell>
                      <Table.Cell className="text-xs text-gray-500">
                        {entry.addedBy || '—'}
                      </Table.Cell>
                      <Table.Cell>
                        <button
                          type="button"
                          onClick={() => handleRemoveEntry(entry.id)}
                          className="text-xs text-red-600 hover:text-red-800"
                        >
                          Remove
                        </button>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
            </Table.ScrollArea>
          )}

          {!loading && (
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={() => void handleSave()}
                disabled={saving}
                className="px-6 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Save Blocklist'}
              </button>
              {saveError !== null && <p className="text-sm text-red-600">{saveError}</p>}
              {saveSuccess && (
                <p className="text-sm text-green-600">Saved. Changes will propagate within 60s.</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
